import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Skeleton,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Section, SettingRow, ActionBar } from '@vnetwork/ui/layout';
import { useToast, Alert } from '@vnetwork/ui/feedback';
import {
  useAffiliateProfile,
  useUpdateAffiliateProfile,
  type AffiliateProfile,
} from '@/api/affiliate';
import { errorMessage } from '@/api/error-message';
import { brand } from '@/config/brand';

type Method = 'bank_transfer' | 'momo' | 'account_credit';

const METHOD_LABEL: Record<Method, string> = {
  bank_transfer: 'Bank transfer',
  momo: 'MoMo wallet',
  account_credit: 'Account credit',
};

function detailString(details: Record<string, unknown> | undefined, key: string): string {
  const v = details?.[key];
  return typeof v === 'string' ? v : '';
}

export function AffiliateSettings() {
  const { show } = useToast();
  const { data: profile, isLoading } = useAffiliateProfile();

  if (isLoading) return <Skeleton style={{ height: 280 }} />;
  if (!profile) return <Alert tone="warning" title="Join the affiliate program first" />;

  return <SettingsForm profile={profile} onSaved={() => show({ tone: 'success', title: 'Payout settings saved' })} />;
}

function SettingsForm({ profile, onSaved }: { profile: AffiliateProfile; onSaved: () => void }) {
  const { show } = useToast();
  const [method, setMethod] = useState<Method>(
    (profile.payout_method as Method) || 'bank_transfer',
  );
  const [bankName, setBankName] = useState(detailString(profile.payout_details, 'bank_name'));
  const [accountNumber, setAccountNumber] = useState(
    detailString(profile.payout_details, 'account_number'),
  );
  const [accountHolder, setAccountHolder] = useState(
    detailString(profile.payout_details, 'account_holder'),
  );
  const [momoPhone, setMomoPhone] = useState(detailString(profile.payout_details, 'phone'));
  const [threshold, setThreshold] = useState(String(profile.payout_threshold ?? 0));
  const [error, setError] = useState<string | null>(null);

  // Re-sync every field when the underlying record changes (e.g. after a save
  // refetch or an edit from another tab). React Query's structural sharing keeps
  // `profile` referentially stable while the content is unchanged, so this does
  // not clobber in-progress edits on unrelated re-renders.
  useEffect(() => {
    setMethod((profile.payout_method as Method) || 'bank_transfer');
    setBankName(detailString(profile.payout_details, 'bank_name'));
    setAccountNumber(detailString(profile.payout_details, 'account_number'));
    setAccountHolder(detailString(profile.payout_details, 'account_holder'));
    setMomoPhone(detailString(profile.payout_details, 'phone'));
    setThreshold(String(profile.payout_threshold ?? 0));
  }, [profile]);

  const update = useUpdateAffiliateProfile({
    onSuccess: onSaved,
    onError: (e) =>
      show({
        tone: 'danger',
        title: 'Could not save settings',
        description: errorMessage(e, 'Please try again.'),
      }),
  });

  function handleSave() {
    setError(null);

    const parsedThreshold = Number(threshold);
    if (!Number.isFinite(parsedThreshold) || parsedThreshold < 0) {
      setError('Payout threshold must be a number of 0 or more.');
      return;
    }

    let details: Record<string, unknown> = {};
    if (method === 'bank_transfer') {
      if (!bankName.trim() || !accountNumber.trim() || !accountHolder.trim()) {
        setError('Please fill in bank name, account number and account holder.');
        return;
      }
      details = {
        bank_name: bankName.trim(),
        account_number: accountNumber.trim(),
        account_holder: accountHolder.trim(),
      };
    } else if (method === 'momo') {
      if (!/^0\d{9}$/.test(momoPhone.trim())) {
        setError('Enter a valid 10-digit MoMo phone number (e.g. 0901234567).');
        return;
      }
      details = { phone: momoPhone.trim() };
    }

    update.mutate({
      payout_method: method,
      payout_details: details,
      payout_threshold: parsedThreshold,
    });
  }

  return (
    <Section title="Payout settings" description="Choose how you'd like to receive your commission.">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        {error && <Alert tone="danger" title={error} />}
        <SettingRow
          title="Payout method"
          description="How your commission is transferred to you."
          control={
            <Select value={method} onValueChange={(v) => setMethod(v as Method)}>
              <SelectTrigger style={{ minWidth: 180 }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(METHOD_LABEL) as Method[]).map((m) => (
                  <SelectItem key={m} value={m}>
                    {METHOD_LABEL[m]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />

        {method === 'bank_transfer' && (
          <SettingRow title="Bank account" description="Where we send your commission.">
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 480, marginTop: 'var(--vn-space-3)' }}>
              <Field label="Bank name">
                {(props) => (
                  <Input {...props} value={bankName} onChange={(e) => setBankName(e.target.value)} />
                )}
              </Field>
              <Field label="Account number">
                {(props) => (
                  <Input {...props} value={accountNumber} onChange={(e) => setAccountNumber(e.target.value)} />
                )}
              </Field>
              <Field label="Account holder">
                {(props) => (
                  <Input {...props} value={accountHolder} onChange={(e) => setAccountHolder(e.target.value)} />
                )}
              </Field>
            </div>
          </SettingRow>
        )}

        {method === 'momo' && (
          <SettingRow title="MoMo" description="The MoMo wallet that receives your commission.">
            <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 480, marginTop: 'var(--vn-space-3)' }}>
              <Field label="MoMo phone number">
                {(props) => (
                  <Input {...props} value={momoPhone} onChange={(e) => setMomoPhone(e.target.value)} />
                )}
              </Field>
            </div>
          </SettingRow>
        )}

        {method === 'account_credit' && (
          <SettingRow
            title="Account credit"
            description={`Your commission will be added to your ${brand.shortName} account credit balance.`}
          />
        )}

        <SettingRow
          title="Payout threshold"
          description="Minimum balance before a payout can be requested."
        >
          <div style={{ display: 'grid', gap: 'var(--vn-space-4)', maxWidth: 480, marginTop: 'var(--vn-space-3)' }}>
            <Field label="Threshold">
              {(props) => (
                <Input
                  {...props}
                  type="number"
                  inputMode="numeric"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                />
              )}
            </Field>
          </div>
        </SettingRow>
      </div>

      <ActionBar
        end={
          <Button size="sm" disabled={update.isPending} onClick={handleSave}>
            {update.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        }
      />
    </Section>
  );
}
